import { useCallback, useState } from "react";
import {
  CheckIcon,
  CircleIcon,
  Clock3Icon,
  CloudIcon,
  LoaderCircleIcon,
  MoonIcon,
  TriangleAlertIcon,
  ZapIcon,
  RefreshCwIcon,
  type LucideIcon,
} from "lucide-react";
import {
  connectCloudProjectRuntime,
  type CloudRuntimeConnectProgressEvent,
} from "../sidebar/connectCloudRuntime";
import type {
  CloudRuntimeRouteBlockKind,
  CloudRuntimeRouteBlockState,
} from "../../routes/ensureActiveRouteCloudRuntime";
import { Button } from "../ui/button";
import { cn } from "~/lib/utils";
import {
  refreshCmmdForgeRuntimeList,
  resolveSingletonConfig,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import { CmmdForgeRuntimeApiClient } from "../../environments/primary/cmmdForgeRuntimeApi";
import { wakeCloudRuntimeFromExecutionIntent } from "../settings/wakeCloudEnvironmentFromExecutionIntent";
import { buildCmmdRuntimeEnvironmentId } from "../../environments/cmmdRuntimeEnvironmentId";
import { EnvironmentId } from "@cmmd-forge/contracts";
import { useForgeCloudEnvironmentLabelForRuntime } from "../../environments/useForgeCloudEnvironmentLabelForRuntime";

interface CloudRuntimeRouteBlockedPanelProps {
  readonly blockedState: CloudRuntimeRouteBlockState;
  readonly variant?: "full" | "banner";
}

type CloudRuntimeRecoveryStatus = "idle" | "starting" | "failed";

interface CloudRuntimeProgressStepProps {
  readonly event: CloudRuntimeConnectProgressEvent;
  readonly isLatest: boolean;
  readonly status: CloudRuntimeRecoveryStatus;
}

interface CloudRuntimeRouteBlockCopy {
  readonly actionLabel: string | null;
  readonly detail: string;
  readonly idleLabel: string;
  readonly icon: LucideIcon;
  readonly progressLabel: string | null;
  readonly requestSummary: string | null;
  readonly title: string;
}

const BLOCK_COPY: Readonly<Record<CloudRuntimeRouteBlockKind, CloudRuntimeRouteBlockCopy>> = {
  // Nothing has failed and nothing is asleep — this project simply has not been
  // opened yet. The copy says what will happen (a workspace gets created) rather
  // than implying recovery from a bad state.
  unprovisioned: {
    actionLabel: "Start project",
    detail:
      "Your cloud project is created the first time you open it. It takes a few minutes to set up, then starts instantly after that.",
    idleLabel: "Waiting for your start command.",
    icon: CloudIcon,
    progressLabel: "Creating...",
    requestSummary: "Creating cloud project",
    title: "This project hasn't started yet",
  },
  sleeping: {
    actionLabel: "Start project",
    detail:
      "It was hibernated to save costs. Start it to run this thread. It takes a few seconds to boot.",
    idleLabel: "Waiting for your start command.",
    icon: MoonIcon,
    progressLabel: "Starting...",
    requestSummary: "Starting cloud project",
    title: "This project is asleep",
  },
  paused: {
    actionLabel: "Resume project",
    detail:
      "This project was paused explicitly. Resume it to allow automatic wake and continue this thread.",
    idleLabel: "Waiting for your resume command.",
    icon: MoonIcon,
    progressLabel: "Resuming...",
    requestSummary: "Resuming cloud project",
    title: "This project is paused",
  },
  error: {
    actionLabel: "Repair project",
    detail:
      "Forge repairs this same cloud environment in place, so your threads and workspace stay in place.",
    idleLabel: "Waiting for your repair command.",
    icon: TriangleAlertIcon,
    progressLabel: "Repairing...",
    requestSummary: "Repairing cloud project in place",
    title: "This project needs repair",
  },
  destroyed: {
    actionLabel: "Recreate project",
    detail: "The previous cloud runtime no longer exists. Recreate it to continue this thread.",
    idleLabel: "Waiting for your recreate command.",
    icon: TriangleAlertIcon,
    progressLabel: "Recreating...",
    requestSummary: "Recreating cloud project",
    title: "This project needs to be recreated",
  },
  draining: {
    actionLabel: null,
    detail:
      "Forge is finishing a project update. Existing work can finish, but new connections are paused until the update completes.",
    idleLabel: "Waiting for the project update to finish.",
    icon: Clock3Icon,
    progressLabel: null,
    requestSummary: null,
    title: "Project update in progress",
  },
  "update-required": {
    actionLabel: "Update Forge",
    detail:
      "This project is running an older Forge build. Update it in place to continue. Your files and conversation history stay intact.",
    idleLabel: "Waiting for your update command.",
    icon: RefreshCwIcon,
    progressLabel: "Updating...",
    requestSummary: "Updating Forge in place",
    title: "Project update required",
  },
};

export type CloudRuntimeBlockedAction =
  | "provision"
  | "wake"
  | "resume"
  | "repair"
  | "recreate"
  | "update"
  | "wait";

export function resolveCloudRuntimeBlockedAction(
  kind: CloudRuntimeRouteBlockKind,
): CloudRuntimeBlockedAction {
  switch (kind) {
    case "unprovisioned":
      return "provision";
    case "sleeping":
      return "wake";
    case "paused":
      return "resume";
    case "error":
      return "repair";
    case "destroyed":
      return "recreate";
    case "update-required":
      return "update";
    case "draining":
      return "wait";
  }
}

async function executeCloudRuntimeBlockedAction(
  blockedState: CloudRuntimeRouteBlockState,
  onProgress: (event: CloudRuntimeConnectProgressEvent) => void,
): Promise<void> {
  const action = resolveCloudRuntimeBlockedAction(blockedState.kind);
  if (action === "wait") return;

  if (action === "update") {
    const runtimeId = blockedState.meta.runtimeId;
    if (runtimeId === null) throw new Error("This project does not have an Environment to update.");
    const client = new CmmdForgeRuntimeApiClient(resolveSingletonConfig());
    await client.updateRuntimeCommit({ runtimeId });
    refreshCmmdForgeRuntimeList();
    return;
  }

  if (action === "wake") {
    const runtimeId = blockedState.meta.runtimeId;
    if (runtimeId === null) throw new Error("This project does not have an Environment to start.");
    await wakeCloudRuntimeFromExecutionIntent({
      environmentId: EnvironmentId.make(
        blockedState.meta.cmmdEnvironmentId ?? buildCmmdRuntimeEnvironmentId(runtimeId),
      ),
      runtimeId,
    });
    refreshCmmdForgeRuntimeList();
    return;
  }

  await connectCloudProjectRuntime(blockedState.meta, { onProgress });
}

function getCloudRuntimeRecoveryError(error: unknown, kind: CloudRuntimeRouteBlockKind): string {
  if (error instanceof Error) return error.message;
  return kind === "update-required" ? "Couldn't update Forge." : "Couldn't start the project.";
}

function CloudRuntimeProgressStep({ event, isLatest, status }: CloudRuntimeProgressStepProps) {
  const isComplete = !isLatest || status === "failed" || event.stage === "connected";
  const isFailed = isLatest && status === "failed";
  const Icon = isFailed ? TriangleAlertIcon : isComplete ? CheckIcon : LoaderCircleIcon;

  return (
    <li className="grid grid-cols-[1rem_1fr] gap-2 text-left">
      <Icon
        className={cn(
          "mt-0.5 size-3.5",
          isFailed
            ? "text-destructive"
            : isComplete
              ? "text-primary"
              : "animate-spin text-muted-foreground",
        )}
        aria-hidden
      />
      <div className="min-w-0">
        <p className="text-[11px] font-medium leading-4 text-foreground">{event.summary}</p>
        {event.detail ? (
          <p className="break-all font-mono text-[10px] leading-4 text-muted-foreground/80">
            {event.detail}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Full-view surface shown when a cloud thread's VM is asleep (hibernated to save
 * cost) or errored, and we deliberately did NOT auto-wake it on open. Waking a
 * A Sprite costs money and takes time to start, so the user starts it explicitly
 * here rather than the app silently spinning one up on every visit. Once the VM
 * connects, the route re-renders and this unmounts, revealing the thread.
 */
/**
 * The panel's detail line. An update acts on a whole Environment, so it names
 * the one it will update: a person with an Environment in each of two
 * Organizations must see which before pressing "Update Forge" (FOR-36902).
 */
function cloudRuntimeBlockDetail(
  kind: CloudRuntimeRouteBlockKind,
  environmentName: string | null,
): string {
  const detail = BLOCK_COPY[kind].detail;
  if (kind !== "update-required" || environmentName === null) return detail;
  return `${detail} This updates ${environmentName}.`;
}

/**
 * What a thread shows while its cloud Environment cannot take work, with the
 * one action that unblocks it. An update names the Environment it will act on.
 */
export function CloudRuntimeRouteBlockedPanel({
  blockedState,
  variant = "full",
}: CloudRuntimeRouteBlockedPanelProps) {
  const [status, setStatus] = useState<CloudRuntimeRecoveryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [progressEvents, setProgressEvents] = useState<CloudRuntimeConnectProgressEvent[]>([]);
  const copy = BLOCK_COPY[blockedState.kind];
  const environmentName = useForgeCloudEnvironmentLabelForRuntime(blockedState.meta.runtimeId);
  const detail = cloudRuntimeBlockDetail(blockedState.kind, environmentName);

  const start = useCallback(async () => {
    if (copy.requestSummary === null) return;
    setStatus("starting");
    setError(null);
    setProgressEvents([
      {
        stage: "connect-request",
        summary: copy.requestSummary,
        detail: blockedState.meta.projectName,
        at: new Date().toISOString(),
      },
    ]);
    try {
      await executeCloudRuntimeBlockedAction(blockedState, (event) => {
        setProgressEvents((current) => [...current, event]);
      });
    } catch (startError) {
      setStatus("failed");
      setError(getCloudRuntimeRecoveryError(startError, blockedState.kind));
    }
  }, [blockedState, copy.requestSummary]);

  const Icon = copy.icon;
  const cta = status === "starting" ? copy.progressLabel : copy.actionLabel;

  if (variant === "banner") {
    return (
      <div className="shrink-0 border-b border-border bg-muted/20 px-6 py-3">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Icon className="size-4 text-primary" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-medium text-foreground">{copy.title}</h2>
              <p className="mt-0.5 text-xs leading-4 text-muted-foreground">{detail}</p>
            </div>
            {cta ? (
              <Button
                className="shrink-0"
                type="button"
                size="sm"
                disabled={status === "starting"}
                onClick={start}
              >
                <ZapIcon className="size-4" aria-hidden />
                {cta}
              </Button>
            ) : null}
          </div>
          {progressEvents.length > 0 ? (
            <ol className="mt-3 space-y-2 border-t border-border/60 pt-3">
              {progressEvents.map((event, index) => (
                <CloudRuntimeProgressStep
                  key={`${event.stage}:${event.at}`}
                  event={event}
                  isLatest={index === progressEvents.length - 1}
                  status={status}
                />
              ))}
            </ol>
          ) : null}
          {status === "failed" && error ? (
            <p className="mt-2 text-[11px] leading-4 text-destructive-foreground">{error}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center px-6">
      <div className="w-full max-w-sm rounded-lg border border-border/60 bg-muted/20 px-5 py-6 text-center">
        <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-primary/10">
          <Icon className="size-5 text-primary" aria-hidden />
        </div>
        <h2 className="text-sm font-medium text-foreground">{copy.title}</h2>
        <p className="mx-auto mt-1.5 max-w-xs text-xs leading-5 text-muted-foreground">{detail}</p>
        {cta ? (
          <div className="mt-4 flex justify-center">
            <Button type="button" size="sm" disabled={status === "starting"} onClick={start}>
              <ZapIcon className="size-4" aria-hidden />
              {cta}
            </Button>
          </div>
        ) : null}
        {progressEvents.length > 0 ? (
          <ol className="mt-4 space-y-2 border-t border-border/60 pt-3">
            {progressEvents.map((event, index) => (
              <CloudRuntimeProgressStep
                key={`${event.stage}:${event.at}`}
                event={event}
                isLatest={index === progressEvents.length - 1}
                status={status}
              />
            ))}
          </ol>
        ) : (
          <div className="mx-auto mt-4 flex max-w-xs items-center justify-center gap-2 text-[11px] text-muted-foreground/60">
            <CircleIcon className="size-3" aria-hidden />
            <span>{copy.idleLabel}</span>
          </div>
        )}
        {status === "failed" && error ? (
          <p className="mt-3 text-[11px] leading-4 text-destructive-foreground">{error}</p>
        ) : null}
        {blockedState.kind === "sleeping" || blockedState.kind === "paused" ? (
          <p className="mt-3 text-[11px] text-muted-foreground/60">
            Your threads are safe. Only the running VM was paused.
          </p>
        ) : null}
      </div>
    </div>
  );
}
